import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import { Input, Modal, Table, Typography } from 'antd';
import React, { useMemo } from 'react';

import { useTagsModal } from '@app/components/TagsModal/hooks/useTagsModal';
import type { NTagsModal } from '@app/components/TagsModal/TagsModal.types';
import { adaptTagsDataToTable } from '@app/components/TagsModal/utils/adaptTagsDataToTable';
import { CANCEL, SAVE } from '@app/constants';
import { RECORDS_TAGS_HEADER, U_KEY } from '@app/constants/tables';

import type { Key, RowSelectionType } from 'antd/es/table/interface';

const TABLE_SCROLL_Y = 400;
const SELECTION_COLUMN_WIDTH = 60;
const DESCRIPTION_COLUMN_WIDTH = 60;
const NAME_COLUMN_WIDTH = 340;
const TABLE_SCROLL_X =
  SELECTION_COLUMN_WIDTH + NAME_COLUMN_WIDTH + DESCRIPTION_COLUMN_WIDTH;

const { Text } = Typography;

export const TagsModal: NTagsModal.Main = ({
  isTagsModalOpen,
  updateTags,
  tagsLoader,
  setIsTagsModalOpen,
  setSelectedTagsIds,
  selectionType,
  selectedTagsIds,
  tags,
}) => {
  const {
    handleTagsSearch,
    searchedTags,
    handleCloseModal,
    searchInputValue,
    setSearchInputValue,
    handleOk,
    handleAfterOpenChange,
  } = useTagsModal({
    tags,
    setIsTagsModalOpen,
    updateTags,
    selectedTagsIds,
    setSelectedTagsIds,
  });

  const tableData = useMemo(
    () => adaptTagsDataToTable(searchedTags),
    [searchedTags]
  );

  const columns = useMemo(
    () =>
      RECORDS_TAGS_HEADER.map((column, index) =>
        index === 0
          ? { ...column, width: NAME_COLUMN_WIDTH }
          : {
              ...column,
              width: DESCRIPTION_COLUMN_WIDTH,
              align: 'center' as const,
            }
      ),
    []
  );

  return (
    <Modal
      data-testid="add-tags-popup"
      afterOpenChange={handleAfterOpenChange}
      title={<span data-testid="add-tags-title">Добавить теги</span>}
      open={isTagsModalOpen}
      cancelText={<span data-testid="cancel">{CANCEL}</span>}
      okText={<span data-testid="save">{SAVE}</span>}
      onOk={handleOk}
      closeIcon={<CloseOutlined data-testid="close-popup" />}
      okButtonProps={{
        type: 'primary',
        loading: tagsLoader,
      }}
      cancelButtonProps={{
        disabled: tagsLoader,
      }}
      onCancel={handleCloseModal}
      className="tags-modal"
    >
      <Input
        data-testid="tag-filter-input"
        placeholder="Введите название тега"
        addonBefore={<SearchOutlined />}
        value={searchInputValue}
        onChange={({ target }) => {
          const { value } = target;
          handleTagsSearch(value);
          setSearchInputValue(value);
        }}
        style={{ marginBottom: '15px', marginTop: '15px', width: '100%' }}
      />
      <Text type="secondary" data-testid="selected-tags-count">
        Выбрано тегов: {selectedTagsIds.length}
      </Text>
      <Table
        data-testid="tag-list"
        className="tags-modal"
        rowClassName="tag-row"
        columns={columns}
        dataSource={tableData}
        rowSelection={{
          preserveSelectedRowKeys: true,
          type: selectionType as RowSelectionType,
          columnWidth: SELECTION_COLUMN_WIDTH,
          onChange: (selectedRowKeys: Key[]) => {
            setSelectedTagsIds(selectedRowKeys.map(String));
          },
          selectedRowKeys: selectedTagsIds,
          renderCell: (checked, record, index, originNode) => {
            return React.cloneElement(originNode as React.ReactElement, {
              'data-testid': checked ? 'checked-tag' : 'tag-checkbox-item',
            });
          },
        }}
        rowKey={(record) => `${record[U_KEY]}`}
        pagination={false}
        virtual
        scroll={{ y: TABLE_SCROLL_Y, x: TABLE_SCROLL_X }}
      />
    </Modal>
  );
};
